import dayjs, { Dayjs } from 'dayjs';
import { ChangeEvent, FC } from 'react';

import Grid from '@mui/material/Grid';
import { SelectChangeEvent } from '@mui/material/Select';
import Stack from '@mui/material/Stack';

import WaveUIAutocomplete from '../WaveUIAutocomplete';
import WaveUICheckbox from '../WaveUICheckbox';
import WaveUIDatePicker from '../WaveUIDatePicker';
import WaveUIInput from '../WaveUIInput';
import WaveUIInputPassword from '../WaveUIInputPassword';
import WaveUIMultiSelect from '../WaveUIMultiSelect';
import WaveUISelect from '../WaveUISelect';
import WaveUISwitch from '../WaveUISwitch';
import FieldType from './types/fieldType.enum';
import FormField from './types/formField.type';

type WaveUIForm_V2Props = {
  fields: FormField[];
  getFieldsValues?: (fieldsValues: Record<string, unknown>) => void;
  getFormStatus?: (status: string) => void;
};

const WaveUIForm_V2: FC<WaveUIForm_V2Props> = ({
  fields,
  getFieldsValues,
  getFormStatus,
}) => {
  const onInputTextChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    getFieldsValues?.({
      [name]: value,
    });
  };

  const onInputNumberChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    getFieldsValues?.({
      [name]: Number(value),
    });
  };

  const onInputCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    getFieldsValues?.({
      [name]: checked,
    });
  };

  const onInputSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    getFieldsValues?.({
      [name]: value,
    });
    getFormStatus?.('dirty');
  };

  const onInputDatePickerChange = (date: Dayjs | null, fieldName: string) => {
    if (date && dayjs.isDayjs(date) && date.isValid()) {
      getFieldsValues?.({
        [fieldName]: date,
      });
      getFormStatus?.('dirty');
    }
  };

  const onInputMultiselectChange = (e: SelectChangeEvent<string[]>) => {
    const { name, value } = e.target;
    getFieldsValues?.({
      [name]: value,
    });
  };

  const onAutocompleteInputChange = (value: string, fieldName: string) => {
    getFieldsValues?.({
      [`${fieldName}:input`]: value,
    });
  };

  const onAutocompleteChange = (option: unknown, fieldName: string) => {
    getFieldsValues?.({
      [fieldName]: option,
    });
  };

  const getInputByFieldType = (field: FormField) => {
    const { field_type, field_props } = field;

    switch (field_type) {
      case FieldType.ColorPicker:
        return (
          <WaveUIInput
            onChange={onInputTextChange}
            type='color'
            {...field_props}
          />
        );
      case FieldType.MultiSelect:
        return (
          <WaveUIMultiSelect
            onChange={onInputMultiselectChange}
            {...field_props}
          />
        );
      case FieldType.DateTime:
        return (
          <WaveUIDatePicker
            onChange={(date) => onInputDatePickerChange(date, field_props.name)}
            withTime
            {...field_props}
          />
        );
      case FieldType.Date:
        return (
          <WaveUIDatePicker
            onChange={(date) => onInputDatePickerChange(date, field_props.name)}
            {...field_props}
          />
        );
      case FieldType.Checkbox:
        return (
          <WaveUICheckbox onChange={onInputCheckboxChange} {...field_props} />
        );
      case FieldType.Select:
        return <WaveUISelect onChange={onInputSelectChange} {...field_props} />;
      case FieldType.Switch:
        return (
          <WaveUISwitch onChange={onInputCheckboxChange} {...field_props} />
        );
      case FieldType.Text:
        return <WaveUIInput onChange={onInputTextChange} {...field_props} />;
      case FieldType.Password:
        return (
          <WaveUIInputPassword
            onChange={onInputTextChange}
            type='password'
            {...field_props}
          />
        );
      case FieldType.Number:
        return (
          <WaveUIInput
            onChange={onInputNumberChange}
            inputProps={{ type: 'number', min: 0 }}
            {...field_props}
          />
        );
      case FieldType.Autocomplete:
        return (
          <WaveUIAutocomplete
            onInputChange={(_, value) =>
              onAutocompleteInputChange(value, field_props.name)
            }
            onChange={(_, option) =>
              onAutocompleteChange(option, field_props.name)
            }
            {...field_props}
          />
        );
      default:
        return null;
    }
  };

  const formFieldsFiltered = fields.filter((item) => item.isVisible !== false);

  return (
    <Stack component='form' onChange={() => getFormStatus?.('dirty')}>
      <Grid container spacing={2}>
        {formFieldsFiltered.map((field, index) => (
          <Grid key={index} item xs={12} sm={field.columns || 12}>
            {field.columnRenderer
              ? field.columnRenderer(getInputByFieldType(field))
              : getInputByFieldType(field)}
          </Grid>
        ))}
      </Grid>
    </Stack>
  );
};

export default WaveUIForm_V2;
